import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  IAddressAutocompleteRequest,
  IAddressDetailsRequest,
  IDealerByNameRequest,
  IDealerDataRegistrationParams,
  IDealerRegistrationRequest,
  INearestDealersRequest,
  IOrganisationListItem,
  IRegisterOrganisationRequestWithMoreParams,
  IRegistrationCurrentOrganisationAddress,
  IShopsRegistrationRequestWithParams,
} from 'cde-fe-organization-registration-dialog';
import { getCountryByAlpha2, getLocaleByAlpha2 } from 'country-locale-map';
import { of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  finalize,
  first,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { IRegisterOrganisationResultWithDealerAndShop, Role } from '@cde-fe-app/app/shared/models/organisation.model';
import { AuthServiceWrapper } from '@cde-fe-app/app/shared/services/auth.service';
import { DealerService } from '@cde-fe-app/app/shared/services/dealer.service';
import { InterceptorService } from '@cde-fe-app/app/shared/services/interceptor.service';
import { MaintenanceTextService } from '@cde-fe-app/app/shared/services/maintenance-text.service';
import { NotificationsService } from '@cde-fe-app/app/shared/services/notifications.service';
import { OrganizationService } from '@cde-fe-app/app/shared/services/organization.service';
import * as AuthActions from '@cde-fe-app/app/shared/state/auth/auth.actions';
import { configsStore } from '@cde-fe-app/app/shared/state/configs/configs.store';
import * as DashboardActions from '@cde-fe-app/app/shared/state/dashboard/dashboard.actions';
import { ResetDashboard } from '@cde-fe-app/app/shared/state/dashboard/dashboard.actions';
import { dealerStore } from '@cde-fe-app/app/shared/state/dealer/dealer.store';
import { featureToggleStore } from '@cde-fe-app/app/shared/state/feature-toggle/feature-toggle.store';
import * as RootState from '../';
import { RegisterOrganisationSuccess } from './organisations.actions';
import * as OrganisationsActions from './organisations.actions';
import { ResetAnalytics } from '../analytics/analytics.actions';
import { ResetConnections } from '../connections/connections.actions';
import { ResetFilters } from '../filter/filter.actions';
import { ResetMachineDetails } from '../machine-details/machine-details.actions';
import { EquipmentList, ResetMachineOverview } from '../machine-overview/machines.actions';
import { remoteCapabilitiesStore } from '../remote-capabilities/remote-capabilities.store';
import { shopsStore } from '../shops/shops.store';

@Injectable()
export class OrganisationsEffects {
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);
  readonly actions$ = inject(Actions);
  readonly organisationService = inject(OrganizationService);
  readonly notificationService = inject(NotificationsService);
  readonly auth = inject(AuthServiceWrapper);
  readonly maintenanceTextService = inject(MaintenanceTextService);
  readonly platformId = inject<number | string>(PLATFORM_ID);
  readonly shopsStoreInstance = inject(shopsStore);
  readonly remoteCapabilitiesStoreInstance = inject(remoteCapabilitiesStore);
  readonly dealerService = inject(DealerService);
  readonly dealerStore = inject(dealerStore);
  readonly configsStore = inject(configsStore);
  readonly featureToggleStore = inject(featureToggleStore);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly interceptorService = inject(InterceptorService);

  rehydrate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganisationsActions.REHYDRATE_STATE),
        map((action: OrganisationsActions.RehydrateState) => action.successfulLogin),
        tap(successfulLogin => {
          this.store.dispatch(new OrganisationsActions.List({ rehydrate: true, successfulLogin }));
          this.featureToggleStore.getFeatureToggles(false);
        })
      ),
    { dispatch: false }
  );

  list$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.LIST),
      withLatestFrom(this.store),
      map(
        (
          data: [OrganisationsActions.List, RootState.IState]
        ): [
          IOrganisationListItem[],
          boolean | undefined,
          string | undefined,
          boolean | undefined,
          boolean | undefined,
        ] => {
          const periodically = data[0].context?.periodically ?? false;
          const rehydrate = data[0].context?.rehydrate;
          const orgState = data[1].organisations;
          const currentOrgId = orgState.lastSuccess?.auth0Id;
          const successfulLogin = data[0].context?.successfulLogin;
          return [orgState.organisationListItems, periodically, currentOrgId, rehydrate, successfulLogin];
        }
      ),
      mergeMap(([storeOrganisations, periodically, currentOrgId, rehydrate, successfulLogin]) =>
        this.organisationService.list().pipe(
          map((organisations: IOrganisationListItem[]) => {
            console.log(
              'Organisation List Success. Periodic Refresh:',
              periodically,
              'Successful Login:',
              successfulLogin,
              'Current Org Id:',
              currentOrgId,
              'Store Orgs Exist:',
              storeOrganisations.length > 0
            );
            if (!periodically) {
              return new OrganisationsActions.ListSuccess(organisations, rehydrate, successfulLogin);
            } else {
              const currentOrgIds = organisations.map(org => org.auth0Id).sort();
              const storeOrgIds = storeOrganisations.map(org => org.auth0Id).sort();
              const localStorageOrgId = window.localStorage.getItem('currentOrganisationId');
              const currentStoreOrg = storeOrganisations.find(storeOrg => storeOrg.auth0Id === currentOrgId);
              // also handle undefined case with == null check
              if (
                currentStoreOrg == null ||
                localStorageOrgId == null ||
                localStorageOrgId !== currentStoreOrg?.auth0Id ||
                JSON.stringify(currentOrgIds) !== JSON.stringify(storeOrgIds)
              ) {
                return new OrganisationsActions.ListSuccess(organisations, rehydrate, successfulLogin);
              }
              return new OrganisationsActions.ListComplete();
            }
          }),
          catchError(error => {
            if (rehydrate && (error.data?.message as string)?.toLowerCase().includes('external interaction required')) {
              return of(new OrganisationsActions.List({ rehydrate: false, periodically, successfulLogin }));
            }

            return of(new OrganisationsActions.ListFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new OrganisationsActions.ListComplete());
          })
        )
      )
    )
  );

  listSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganisationsActions.LIST_SUCCESS),
        map(
          (data: { payload: IOrganisationListItem[]; rehydrate?: boolean; successfulLogin?: boolean }) =>
            [data.payload, data.rehydrate, data.successfulLogin] as [IOrganisationListItem[], boolean, boolean]
        ),
        tap(([organisations, rehydrate, successfulLogin]: [IOrganisationListItem[], boolean, boolean]) => {
          let checkWithNewToken = false;
          if (isPlatformBrowser(this.platformId)) {
            this.auth
              .getTokenSilently$()
              .pipe(first())
              .subscribe(token => {
                // Resolve target orgId in this order:
                // 1) orgId query param (highest precedence), either the auth0Id
                //    or the database id of the organisation (report links)
                // 2) currentOrganisationId from localStorage
                // If the chosen orgId is missing or not in the fetched list,
                // fall back to the token's org (when it exists in the list),
                // otherwise use the first organisation.
                // If storage org differs from token org, request a new token
                // for the chosen orgId before continuing.
                const queryParams = new URLSearchParams(window.location.search);
                const orgIdParam = queryParams.get('orgId') ?? this.activatedRoute.snapshot.queryParams['orgId'];
                const queryParamOrgId =
                  orgIdParam != null
                    ? (organisations.find(org => org.id === orgIdParam)?.auth0Id ?? orgIdParam)
                    : orgIdParam;
                const localStorageOrgId = queryParamOrgId ?? window.localStorage.getItem('currentOrganisationId');
                const tokenOrgId = this.auth.getAuth0OrgFromToken(token);
                const storageOrgExists = organisations.some(org => org.auth0Id === localStorageOrgId);
                const storageOrgAndTokenOrgDiffer = localStorageOrgId != null && localStorageOrgId !== tokenOrgId;
                if (!storageOrgExists || storageOrgAndTokenOrgDiffer) {
                  // function objects to avoid unneeded loops if storage org
                  // exists already
                  const tokenOrgExists = () => organisations.some(org => org.auth0Id === tokenOrgId);
                  const fallbackOrgId = () => (tokenOrgExists() ? tokenOrgId : organisations?.[0]?.auth0Id);
                  const newOrgId = storageOrgExists ? localStorageOrgId : fallbackOrgId();

                  checkWithNewToken = true;
                  this.auth
                    .getNewToken$(newOrgId)
                    .pipe(first())
                    .subscribe({
                      next: (newToken: string) => {
                        this.store.dispatch(
                          new OrganisationsActions.SetCurrentOrganisation(
                            {
                              auth0Id: newOrgId,
                            },
                            rehydrate != null ? !rehydrate : true,
                            rehydrate,
                            rehydrate,
                            successfulLogin
                          )
                        );
                        this.setUserPermissions(newToken);
                        this.maintenanceTextService.getMaintenanceTexts();
                      },
                      error: (error: Record<string, string>) => {
                        // it can happen that the token becomes invalid when changing an org. Then a relogin needs to be triggered
                        console.error('Token error', error);
                        if (error?.['error_description']?.toLowerCase().includes('external interaction required')) {
                          this.auth.relogin();
                        }
                      },
                    });
                } else {
                  this.store.dispatch(
                    new OrganisationsActions.SetCurrentOrganisation(
                      {
                        auth0Id: tokenOrgId,
                      },
                      rehydrate != null ? !rehydrate : true,
                      rehydrate,
                      rehydrate,
                      successfulLogin
                    )
                  );
                  this.maintenanceTextService.getMaintenanceTexts();
                }

                if (!checkWithNewToken) {
                  this.setUserPermissions(token);
                }
              });
          } else {
            this.store.dispatch(new OrganisationsActions.RehydrateStateComplete());
          }
        })
      ),
    { dispatch: false }
  );

  getNotificationCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_NOTIFICATION_COUNT),
      switchMap(() =>
        this.notificationService.getCountForOrganisations().pipe(
          map(res => new OrganisationsActions.GetNotificationCountSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetNotificationCountFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetNotificationCountComplete()))
        )
      )
    )
  );

  setCurrentOrganisation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganisationsActions.SET_CURRENT_ORGANISATION),
        map(
          (action: OrganisationsActions.SetCurrentOrganisation) =>
            [
              action.payload,
              action.preventRefresh,
              action.removeBackdrop,
              action.rehydrate,
              action.successfulLogin,
            ] as [Partial<IOrganisationListItem> | null, boolean, boolean, boolean, boolean]
        ),
        tap(
          ([organisation, preventRefresh, removeBackdrop, rehydrate, successfulLogin]: [
            Partial<IOrganisationListItem> | null,
            boolean,
            boolean,
            boolean,
            boolean,
          ]) => {
            if (organisation != null) {
              // clear the cache to make sure we get the latest data for the new
              // organisation
              this.interceptorService.clearCache();
              if (!removeBackdrop) {
                this.store.dispatch(new OrganisationsActions.SetOrganisationChanging(true));
              }

              if (!preventRefresh) {
                // no need to get new token beforehand using
                // this.auth.getNewToken$() as GetUser already handles that
                this.store.dispatch(new AuthActions.GetUser(organisation.auth0Id, true));

                this.completeSetCurrentOrg(preventRefresh, successfulLogin, organisation);

                if (rehydrate) {
                  this.store.dispatch(new OrganisationsActions.RehydrateStateComplete());
                }
              } else {
                // this.restartStore();
                this.completeSetCurrentOrg(preventRefresh, successfulLogin, organisation);
                this.store.dispatch(new EquipmentList());
                this.store.dispatch(new OrganisationsActions.RehydrateStateComplete());
              }
            } else {
              this.completeSetCurrentOrg(preventRefresh, successfulLogin, organisation);
            }
          }
        )
      ),
    { dispatch: false }
  );

  getUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.GET_USER_SUCCESS),
        withLatestFrom(this.store),
        tap(([action, store]: [AuthActions.GetUserSuccess, RootState.IState]) => {
          const authState = store.auth;
          const token = authState.token === undefined ? null : authState.token;
          this.setUserPermissions(token);

          if (action.setNewCurrentOrg) {
            this.restartStore();
            this.store.dispatch(new EquipmentList());
          }
        })
      ),
    { dispatch: false }
  );

  getRegistrationDefaultOrganisation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_REGISTRATION_DEFAULT_ORGANISATION),
      map((action: OrganisationsActions.GetRegistrationDefaultOrganisation) => action.country),
      switchMap((country: string) =>
        this.organisationService.getRegistrationDefaultOrganisation(country).pipe(
          map(res => new OrganisationsActions.GetRegistrationDefaultOrganisationSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetRegistrationDefaultOrganisationFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetRegistrationDefaultOrganisationComplete()))
        )
      )
    )
  );

  getCurrentOrganisation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_CURRENT_ORGANISATION),
      map((action: OrganisationsActions.GetCurrentOrganisation) => action.orgId),
      switchMap((orgId: string) =>
        this.organisationService.getCurrentOrganisation(orgId).pipe(
          map(res => {
            this.store.dispatch(new OrganisationsActions.SetOrganisationChanging(false));
            this.store.dispatch(new OrganisationsActions.SetOrganisationLocale(res.country));
            return new OrganisationsActions.GetCurrentOrganisationSuccess(res);
          }),
          catchError(error => of(new OrganisationsActions.GetCurrentOrganisationFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetCurrentOrganisationComplete()))
        )
      )
    )
  );

  // the current organisation is fetched again on every dashboard visit, only load the
  // form fields when none are stored for its country; this also retries a failed request
  // the next time the organisation is loaded
  getCurrentOrganisationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_CURRENT_ORGANISATION_SUCCESS),
      withLatestFrom(this.store),
      map(
        ([action, store]: [OrganisationsActions.GetCurrentOrganisationSuccess, RootState.IState]) =>
          [action.payload.country, store.organisations.currentOrganisationFormFields?.country] as const
      ),
      filter(([country, storedCountry]) => !!country && country !== storedCountry),
      map(([country]) => new OrganisationsActions.GetCurrentOrganisationFormFields(country))
    )
  );

  getCurrentOrganisationFormFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_CURRENT_ORGANISATION_FORM_FIELDS),
      map((action: OrganisationsActions.GetCurrentOrganisationFormFields) => action.country),
      switchMap((country: string) =>
        this.organisationService.getRegistrationFormFields(country).pipe(
          map(res => new OrganisationsActions.GetCurrentOrganisationFormFieldsSuccess(country, res)),
          catchError(error => of(new OrganisationsActions.GetCurrentOrganisationFormFieldsFailure(error)))
        )
      )
    )
  );

  getAddressAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_ADDRESS_AUTOCOMPLETE),
      map((action: OrganisationsActions.GetAddressAutocomplete) => action.payload),
      mergeMap((addressAutocompleteRequest: IAddressAutocompleteRequest) =>
        this.organisationService.getAddressAutocomplete(addressAutocompleteRequest).pipe(
          map(response => new OrganisationsActions.GetAddressAutocompleteSuccess(response)),
          catchError(error => of(new OrganisationsActions.GetAddressAutocompleteFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetAddressAutocompleteComplete()))
        )
      )
    )
  );

  getOrganizationMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_MEMBERS),
      map((action: OrganisationsActions.GetOrganizationMembers) => action.payload),
      switchMap((orgId: string) =>
        this.organisationService.getMembersOfOrganization(orgId).pipe(
          map(res => new OrganisationsActions.GetOrganizationMembersSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetRegistrationDefaultOrganisationFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetOrganizationMembersComplete()))
        )
      )
    )
  );

  registerOrganisation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.REGISTER_ORGANISATION),
      map(
        (action: OrganisationsActions.RegisterOrganisation) =>
          [action.payload, action.removeBackdrop] as [IRegisterOrganisationRequestWithMoreParams, boolean]
      ),
      tap(([_registerOrganisationRequest, removeBackdrop]: [IRegisterOrganisationRequestWithMoreParams, boolean]) => {
        if (!removeBackdrop) {
          this.store.dispatch(new OrganisationsActions.SetOrganisationChanging(true));
        }
      }),
      switchMap(
        ([registerOrganisationRequest, removeBackdrop]: [IRegisterOrganisationRequestWithMoreParams, boolean]) =>
          this.organisationService.registerOrganisation(registerOrganisationRequest).pipe(
            map(
              response =>
                new OrganisationsActions.RegisterOrganisationSuccess(
                  {
                    response,
                    // demo org cannot be upgraded anymore and thus we always create
                    // a new organisation
                    newOrganisation: true,
                    country: registerOrganisationRequest.country,
                    shops: registerOrganisationRequest.request.shops,
                    dealer: registerOrganisationRequest.request.dealer,
                    organisationAddress: registerOrganisationRequest.request.address,
                  },
                  removeBackdrop
                )
            ),
            catchError(error => {
              this.store.dispatch(new OrganisationsActions.SetOrganisationChanging(false));
              return of(new OrganisationsActions.RegisterOrganisationFailure(error));
            }),
            finalize(() => this.store.dispatch(new OrganisationsActions.RegisterOrganisationComplete()))
          )
      )
    )
  );

  registerOrganisationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganisationsActions.REGISTER_ORGANISATION_SUCCESS),
        map(
          (action: RegisterOrganisationSuccess) =>
            [action.payload, action.removeBackdrop] as [IRegisterOrganisationResultWithDealerAndShop, boolean]
        ),
        delay(0), // ensure cache refresh for retrieving a new user token and dispatching a /authorize call
        tap(([payload, removeBackdrop]: [IRegisterOrganisationResultWithDealerAndShop, boolean]) => {
          // set current organisation is not triggered by the dialog anymore as
          // the corresponding event is triggered only when the newly created
          // org is registered as changed and then the SetCurrentOrg action is
          // dispatched. This does not happen anymore as the dialog is closed
          // and thus the event is not triggered.
          this.store.dispatch(
            new OrganisationsActions.SetCurrentOrganisation(
              {
                auth0Id: payload.response.auth0Id,
              },
              false,
              removeBackdrop
            )
          );
        }),
        delay(0),
        tap(([payload, _removeBackdrop]: [IRegisterOrganisationResultWithDealerAndShop, boolean]) => {
          this.store.dispatch(new AuthActions.GetUser(payload.response.auth0Id, true));
          this.store.dispatch(new OrganisationsActions.GetCurrentOrganisation(payload.response.auth0Id));
          this.store.dispatch(new OrganisationsActions.GetRegistrationDefaultOrganisation(payload.country));
          this.store.dispatch(new OrganisationsActions.GetRegistrationShopData(payload.country));
          this.store.dispatch(
            new DashboardActions.GetDashboardsAfterOrgCreation(
              payload.response.id,
              payload.shops.map(s => s.shopId),
              payload.dealer,
              payload.organisationAddress as IRegistrationCurrentOrganisationAddress
            )
          );
          this.store.dispatch(new OrganisationsActions.List());
        })
      ),
    { dispatch: false }
  );

  getRegistrationDealerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_REGISTRATION_DEALER_DATA),
      map((action: OrganisationsActions.GetRegistrationDealerData) => action.payload),
      switchMap((params: IDealerDataRegistrationParams) =>
        this.organisationService.getRegistrationDealerData(params).pipe(
          map(res => new OrganisationsActions.GetRegistrationDealerDataSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetRegistrationDealerDataFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetRegistrationDealerDataComplete()))
        )
      )
    )
  );

  registerOrganisationDealer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.REGISTER_ORGANISATION_DEALER),
      map(
        (action: OrganisationsActions.RegisterOrganisationDealer) =>
          [action.payload, action.language] as [IDealerRegistrationRequest, string]
      ),
      switchMap(([request, language]: [IDealerRegistrationRequest, string]) =>
        this.organisationService.registerOrganisationDealer(request, language).pipe(
          map(response => new OrganisationsActions.RegisterOrganisationDealerSuccess(response)),
          catchError(error => of(new OrganisationsActions.RegisterOrganisationDealerFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.RegisterOrganisationDealerComplete()))
        )
      )
    )
  );

  getRegistrationShopData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_REGISTRATION_SHOP_DATA),
      map((action: OrganisationsActions.GetRegistrationShopData) => ({ country: action.payload, orgId: action.orgId })),
      switchMap(({ country, orgId }) =>
        this.organisationService.getRegistrationShopData(country, orgId).pipe(
          map(res => new OrganisationsActions.GetRegistrationShopDataSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetRegistrationShopDataFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetRegistrationShopDataComplete()))
        )
      )
    )
  );

  registerOrganisationShops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.REGISTER_ORGANISATION_SHOPS),
      map(
        (action: OrganisationsActions.RegisterOrganisationShops) =>
          [action.payload, action.isChangeRequest] as [IShopsRegistrationRequestWithParams, boolean]
      ),
      switchMap(([requestParam, isChangeRequest]: [IShopsRegistrationRequestWithParams, boolean]) =>
        this.organisationService.registerOrganisationShops(requestParam.request, requestParam.country).pipe(
          map(response => new OrganisationsActions.RegisterOrganisationShopsSuccess(response, isChangeRequest)),
          catchError(error => of(new OrganisationsActions.RegisterOrganisationShopsFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.RegisterOrganisationShopsComplete()))
        )
      )
    )
  );

  refreshAfterOrgChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OrganisationsActions.SET_CURRENT_ORGANISATION_SUCCESS,
        OrganisationsActions.REGISTER_ORGANISATION_SHOPS_SUCCESS
      ),
      withLatestFrom(this.store),
      map((context: [Action, RootState.IState]) => context[1].organisations.currentOrganisation?.country ?? ''),
      filter((country: string) => country.length > 0),
      map((country: string) => new OrganisationsActions.GetRegistrationShopData(country))
    )
  );

  getRegistrationDialogSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_REGISTRATION_DIALOG_STEPS),
      switchMap(() =>
        this.organisationService.getRegistrationDialogSteps().pipe(
          map(res => new OrganisationsActions.GetRegistrationDialogStepsSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetRegistrationDialogStepsFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetRegistrationDialogStepsComplete()))
        )
      )
    )
  );

  getNearestDealers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_NEAREST_DEALERS),
      map((action: OrganisationsActions.GetNearestDealers) => action.payload),
      switchMap((request: INearestDealersRequest) =>
        this.organisationService.getNearestDealers(request).pipe(
          map(res => new OrganisationsActions.GetNearestDealersSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetNearestDealersFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetNearestDealersComplete()))
        )
      )
    )
  );

  getDealersByName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_DEALERS_BY_NAME),
      map((action: OrganisationsActions.GetDealersByName) => action.payload),
      switchMap((request: IDealerByNameRequest) =>
        this.organisationService.getDealersByName(request).pipe(
          map(res => new OrganisationsActions.GetDealersByNameSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetDealersByNameFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetDealersByNameComplete()))
        )
      )
    )
  );

  getOrganizationsConnectedDealers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_ORGANISATION_CONNECTED_DEALERS),
      map((action: OrganisationsActions.GetOrganisationConnectedDealers) => action.payload),
      switchMap((request: string) =>
        this.organisationService.getOrganisationsConnectedDealers(request).pipe(
          map(res => new OrganisationsActions.GetOrganisationConnectedDealersSuccess(res)),
          catchError(error => of(new OrganisationsActions.GetOrganisationConnectedDealersFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetOrganisationConnectedDealersComplete()))
        )
      )
    )
  );

  getAddressDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_ADDRESS_DETAILS),
      map((action: OrganisationsActions.GetAddressDetails) => action.payload),
      mergeMap((addressDetailsRequest: IAddressDetailsRequest) =>
        this.organisationService.getAddressDetails(addressDetailsRequest).pipe(
          map(response => new OrganisationsActions.GetAddressDetailsSuccess(response)),
          catchError(error => of(new OrganisationsActions.GetAddressDetailsFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetAddressDetailsComplete()))
        )
      )
    )
  );

  getOrganisationRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.GET_ORGANISATION_ROLES),
      withLatestFrom(this.store),
      map(
        (context: [OrganisationsActions.GetOrganisationRoles, RootState.IState]) =>
          [context[0].roles, context[0].userId ?? context[1].auth.user?.uuid] as [Role[], string]
      ),
      mergeMap(([roles, userId]: [Role[], string]) =>
        this.organisationService.getOrganisationRoles(roles, userId).pipe(
          map(organisationRoles => new OrganisationsActions.GetOrganisationRolesSuccess(organisationRoles)),
          catchError(error => of(new OrganisationsActions.GetOrganisationRolesFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.GetOrganisationRolesComplete()))
        )
      )
    )
  );

  setOrganisationLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.SET_ORGANISATION_LOCALE),
      map((action: OrganisationsActions.SetOrganisationLocale) => action.payload),
      map(country => {
        const locale = getLocaleByAlpha2(country.toUpperCase());
        if (!locale) {
          return 'en-gb';
        }

        // kyrillic locales can be built up like "kk_Cyrl_KZ". If we have more
        // than the usual lang_country locale we calculate the language as the
        // fallback
        if (locale.split('_').length > 2) {
          const countryData = getCountryByAlpha2(country.toUpperCase());
          return countryData?.languages[0];
        }

        return locale?.replace('_', '-') ?? 'en-gb';
      }),
      map(locale => new OrganisationsActions.SetOrganisationLocaleSuccess(locale))
    )
  );

  searchAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.SEARCH_ADDRESS),
      map((action: OrganisationsActions.SearchAddress) => action.request),
      mergeMap((request: IAddressAutocompleteRequest) =>
        this.organisationService.searchAddress(request).pipe(
          map(response => new OrganisationsActions.SearchAddressSuccess(response)),
          catchError(error => of(new OrganisationsActions.SearchAddressFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.SearchAddressComplete()))
        )
      )
    )
  );

  searchDealersByGeoCoords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.SEARCH_NEAREST_DEALERS_BY_GEO_COORDS),
      mergeMap((action: OrganisationsActions.SearchNearestDealersByGeoCoords) =>
        this.dealerService.getDealersByGeoCoordinates(action.request).pipe(
          map(response => new OrganisationsActions.SearchNearestDealersByGeoCoordsSuccess(response)),
          catchError(error => of(new OrganisationsActions.SearchNearestDealersByGeoCoordsFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.SearchNearestDealersByGeoCoordsComplete()))
        )
      )
    )
  );

  searchDealersByAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationsActions.SEARCH_DEALERS_BY_ADDRESS),
      mergeMap((action: OrganisationsActions.SearchDealersByAddress) =>
        this.dealerService.getDealersByAddress(action.request).pipe(
          map(response => new OrganisationsActions.SearchDealersByAddressSuccess(response)),
          catchError(error => of(new OrganisationsActions.SearchDealersByAddressFailure(error))),
          finalize(() => this.store.dispatch(new OrganisationsActions.SearchDealersByAddressComplete()))
        )
      )
    )
  );

  refreshFeatureTogglesAfterCurrentOrgChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrganisationsActions.SET_CURRENT_ORGANISATION_SUCCESS),
        map((action: OrganisationsActions.SetCurrentOrganisationSuccess) => action.successfulLogin),
        tap(successfulLogin => {
          this.featureToggleStore.getFeatureToggles(successfulLogin ?? false);
        })
      ),
    { dispatch: false }
  );

  private completeSetCurrentOrg(
    preventRefresh: boolean,
    successfulLogin: boolean,
    organisation: Partial<IOrganisationListItem> | null
  ) {
    if (preventRefresh) {
      this.store.dispatch(new OrganisationsActions.SetOrganisationChanging(false));
    }

    this.store.dispatch(
      new OrganisationsActions.SetCurrentOrganisationSuccess(
        {
          auth0Id: organisation?.auth0Id,
          name: organisation?.name,
          default: organisation?.default,
          notificationCount: organisation?.notificationCount,
          timeZone: organisation?.timeZone,
        },
        successfulLogin
      )
    );
  }

  private setUserPermissions(token: string | null) {
    const permissions = this.auth.getAuth0UserPermsFromToken(token);
    this.store.dispatch(new OrganisationsActions.SetUserPermissionsSuccess(permissions));
    this.store.dispatch(new OrganisationsActions.GetNotificationCount());
  }

  private restartStore() {
    // RESET STORE
    this.store.dispatch(new ResetAnalytics());
    this.configsStore.resetConfigurations();
    this.store.dispatch(new ResetConnections());
    this.store.dispatch(new ResetDashboard());
    this.dealerStore.resetDealerState();
    this.store.dispatch(new ResetFilters());
    this.store.dispatch(new ResetMachineDetails());
    this.store.dispatch(new ResetMachineOverview());
    this.shopsStoreInstance.resetShops();
    this.remoteCapabilitiesStoreInstance.reset();
  }
}
